import { useEffect, useRef, useCallback } from 'react';

interface UseInactivityTimerProps {
  onInactive: () => void;
  inactivityDelay?: number; // in milliseconds
  enabled?: boolean; // only run timer when enabled (e.g., when there are messages)
}

/**
 * Custom hook to track user inactivity and trigger a callback after a specified delay
 * @param onInactive - Callback function to execute when user is inactive
 * @param inactivityDelay - Time in milliseconds before considering user inactive (default: 15000ms = 15s)
 * @param enabled - Whether the timer should be active (default: true)
 */
export function useInactivityTimer({
  onInactive,
  inactivityDelay = 15000,
  enabled = true,
}: UseInactivityTimerProps) {
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Reset the inactivity timer
  const resetTimer = useCallback(() => {
    // Clear existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // Only set new timer if enabled
    if (enabled) {
      timerRef.current = setTimeout(() => {
        onInactive();
      }, inactivityDelay);
    }
  }, [onInactive, inactivityDelay, enabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  // Reset timer when enabled state changes
  useEffect(() => {
    if (enabled) {
      resetTimer();
    } else {
      // Clear timer when disabled
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [enabled, resetTimer]);

  return { resetTimer };
}
